import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface ThemeContextType {
    isDarkMode: boolean;
    toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        // Always force light mode - dark mode is disabled
        // Clear any cached dark mode preference
        localStorage.removeItem('darkMode');
        return false;
    });

    useEffect(() => {
        // Always ensure light mode is active
        // Remove dark-mode class if it was somehow added
        document.documentElement.classList.remove('dark-mode');
        
        // Also remove from body if it was added there
        document.body.classList.remove('dark-mode');
        
        // Clear localStorage to ensure no cached dark mode preference
        localStorage.removeItem('darkMode');
    }, []);

    const toggleDarkMode = () => {
        setIsDarkMode(prev => !prev);
    };

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
